/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  User, Phone, Calendar,  Globe, FileText,
 Edit, Save, X, AlertTriangle, Bot, MessageSquare,
  BookOpen, Stethoscope, Loader2,
} from 'lucide-react';
import { Patient } from '../../types';
import { useParams } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
import { useGetUserById, useUpdateUserById } from '../../services/query/user/user';
import { showToast } from '../../lib/utils/toast';
import type {  Langs, UpdateUserPayload } from '../../services/api/user/userApi';
import CommunicationHistory from './CommunicationHistory';
import EducationalHistory from './EducationalHistory';
import OverviewTab from './OverviewTab';
import ClinicalTab from './ClinicalTab';
import BillingTab, { BillingSummary } from './BillingTab';
import { languageCodeToName } from '../../lib/utils/helpers';

type TabType = 'overview' | 'clinical' | 'communications' | 'education' | 'billing';


// Removed large demo fallback patient and billing data — UI now uses minimal defaults

const toDisplayName = (value: any): string => {
  if (!value) return 'Unknown';
  if (typeof value.name === 'string' && value.name.trim().length > 0) return value.name;
  const first = typeof value.first_name === 'string' ? value.first_name : '';
  const last = typeof value.last_name === 'string' ? value.last_name : '';
  const full = `${first} ${last}`.trim();
  return full || 'Unknown';
};

const normalizePatient = (value: any): Patient => {
  const insurance = {
    provider: (value?.insurance?.provider as string) ?? '',
    policyNumber: (value?.insurance?.policyNumber as string) ?? '',
    groupNumber: (value?.insurance?.groupNumber as string) ?? '',
  };

  const emergencyContact = {
    name: (value?.emergencyContact?.name as string) ?? (value?.emergency_contact_name as string) ?? '',
    phone: (value?.emergencyContact?.phone as string) ?? (value?.emergency_contact_phone as string) ?? '',
    relationship:
      (value?.emergencyContact?.relationship as string) ?? (value?.emergency_contact_relationship as string) ?? '',
  };

  const dentalChart = {
    id: value?.dentalChart?.id ?? value?.dental_chart?.id ?? '',
    lastUpdated: value?.dentalChart?.lastUpdated ?? value?.dental_chart?.lastUpdated ?? '',
    notes: value?.dentalChart?.notes ?? value?.dental_chart?.notes ?? '',
    teeth: Array.isArray(value?.dentalChart?.teeth)
      ? value.dentalChart.teeth
      : Array.isArray(value?.dental_chart?.teeth)
      ? value.dental_chart.teeth
      : [],
  };

  return {
    id: value?.id ?? String(value?.id ?? ''),
    name: toDisplayName(value),
    phone: value?.phone ?? value?.mobile ?? '',
    email: value?.email ?? '',
    dateOfBirth: value?.dob ?? value?.dateOfBirth ?? '',
    address: value?.address ?? '',
    insurance,
    medicalHistory: Array.isArray(value?.medical_history) ? value.medical_history : Array.isArray(value?.medicalHistory) ? value.medicalHistory : [],
    allergies: Array.isArray(value?.allergies) ? value.allergies : [],
    lastVisit: value?.last_visit ?? value?.lastVisit ?? '',
    nextAppointment: value?.next_appointment ?? value?.nextAppointment ?? undefined,
    emergencyContact,
    language: value?.language === 'es' ? 'es' : 'en',
    notes: value?.notes ?? '',
    treatmentPlans: Array.isArray(value?.treatment_plans) ? value.treatment_plans : Array.isArray(value?.treatmentPlans) ? value.treatmentPlans : [],
    smsLogs: Array.isArray(value?.sms_logs) ? value.sms_logs : Array.isArray(value?.smsLogs) ? value.smsLogs : [],
    emailLogs: Array.isArray(value?.email_logs) ? value.email_logs : Array.isArray(value?.emailLogs) ? value.emailLogs : [],
    dentalChart,
    voiceToChartingNotes: Array.isArray(value?.voice_to_charting_notes)
      ? value.voice_to_charting_notes
      : Array.isArray(value?.voiceToChartingNotes)
      ? value.voiceToChartingNotes
      : [],
    predictiveTreatmentSuggestions: Array.isArray(value?.predictive_treatment_suggestions)
      ? value.predictive_treatment_suggestions
      : [],
    aiSuggestedMedicalAlerts: Array.isArray(value?.ai_suggested_medical_alerts) ? value.ai_suggested_medical_alerts : [],
    aiCreated: value?.ai_created ?? false,
    createdAt: value?.created_at ?? value?.createdAt ?? undefined,
    createdBy: value?.created_by ?? undefined,
    educationHistory: Array.isArray(value?.education_history) ? value.education_history : [],
  } as Patient;
};

export default function PatientProfileNew() {
  const { 
    state, 
    // dispatch 
  } = useAppContext();
  const { selectedPatient, calls, omnichannelMessages } = state;
  const params = useParams();

  const paramId = params?.id ? Number(params.id) : undefined;
  const selectedId = selectedPatient?.id ? Number(selectedPatient.id) : paramId;
  const { data: apiUser } = useGetUserById(selectedId);
  const { mutateAsync: updateUserMutateAsync, isPending: isUpdatingUser } = useUpdateUserById();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editedPatient, setEditedPatient] = useState<Patient | null>(null);

  // NOTE: While API integration is in progress, `selectedPatient` may not match the full `Patient` shape.
  // Normalize it (or fall back to local dummy data) so the UI never crashes.
  const basePatient = React.useMemo(() => {
    const normalized = normalizePatient(selectedPatient);
    if (!apiUser) return normalized;
    // merge API user (UserPatient) into normalized patient for personal details
    const merged = {
      ...normalized,
      id: String(apiUser.id),
      name: toDisplayName(apiUser),
      email: apiUser.email ,
      phone: apiUser.phone,
      dateOfBirth: apiUser.dob,
      address: apiUser.address,
      createdAt: apiUser.created_at,
      // map emergency contact fields from API user shape
      emergencyContact: {
        name: apiUser.emergency_contact_name ?? normalized.emergencyContact.name,
        phone: apiUser.emergency_contact_phone ?? normalized.emergencyContact.phone,
        relationship: apiUser.emergency_contact_relationship ?? normalized.emergencyContact.relationship,
      },
      // map insurance fields from API user shape
      insurance: {
        provider: apiUser.insurance_provider ?? normalized.insurance.provider,
        policyNumber: apiUser.insurance_policy_number ?? normalized.insurance.policyNumber,
        groupNumber: apiUser.insurance_group_number ?? normalized.insurance.groupNumber,
      },
      preferred_language: apiUser.preferred_language ?? normalized.preferred_language,
      medical_condition: apiUser.medical_condition ?? normalized.medical_condition,
    } as Patient;
    return merged;
  }, [selectedPatient, apiUser]);
  const billing = React.useMemo<BillingSummary>(
    () => ({
      accountBalance: 0,
      lastPayment: { date: '', amount: 0, method: 'card' },
      nextDueDate: '',
      records: [],
    }),
    [],
  );

	const patientCalls = (calls ?? []).filter(call => String(call.patientId) === String(basePatient.id));
	const patientMessages = (omnichannelMessages ?? []).filter(msg => String(msg.patientId) === String(basePatient.id));

  const handleEdit = () => {
    setEditedPatient({ ...basePatient });
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!editedPatient) return;
    // build UpdateUserPayload from edited patient
    const names = (editedPatient.name || '').split(' ');
    const payload: Partial<UpdateUserPayload> = {
      first_name: names.slice(0, -1).join(' ') || names[0] || undefined,
      last_name: names.length > 1 ? names[names.length - 1] : undefined,
      email: editedPatient.email || undefined,
      dob: editedPatient.dateOfBirth || undefined,
      address: editedPatient.address || undefined,
      emergency_contact_name: editedPatient.emergencyContact?.name || undefined,
      emergency_contact_phone: editedPatient.emergencyContact?.phone || undefined,
      emergency_contact_relationship: editedPatient.emergencyContact?.relationship || undefined,
      insurance_provider: editedPatient.insurance?.provider || undefined,
      insurance_policy_number: editedPatient.insurance?.policyNumber || undefined,
      insurance_group_number: editedPatient.insurance?.groupNumber || undefined,
      preferred_language: editedPatient.preferred_language || undefined,
      medical_condition: editedPatient.medical_condition || undefined,
    };

    const id = Number(basePatient.id);
    if (!id) {
      setIsEditing(false);
      return;
    }

    // helper to parse many common date formats and return ISO date string (YYYY-MM-DD)
    const formatDateForApi = (input?: string): string | null => {
      if (!input) return null;
      // already ISO
      if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
      // try native Date parse
      const dt = new Date(input);
      if (!isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
      // try slash formats (MM/DD/YYYY or DD/MM/YYYY)
      const m = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (m) {
        const a = m[1];
        const b = m[2];
        const y = m[3];
        const try1 = new Date(`${a}/${b}/${y}`);
        if (!isNaN(try1.getTime())) return try1.toISOString().slice(0, 10);
        const try2 = new Date(`${b}/${a}/${y}`);
        if (!isNaN(try2.getTime())) return try2.toISOString().slice(0, 10);
      }
      return null;
    };

    // format dob if present; if invalid, show toast and abort
    if (payload.dob) {
      const formatted = formatDateForApi(payload.dob as string);
      if (!formatted) {
        showToast.error('Invalid date format for Date of Birth. Please use YYYY-MM-DD or MM/DD/YYYY.');
        return;
      }
      payload.dob = formatted;
    }

    const promise = updateUserMutateAsync({ id, payload });
    showToast.promise(promise, {
      loading: 'Updating patient...',
      success: 'Patient updated successfully',
      error: 'Failed to update patient',
    });

    promise.then(() => setIsEditing(false)).catch(() => {});
  };

  const handleCancel = () => {
    setEditedPatient(null);
    setIsEditing(false);
  };

  const currentPatient = isEditing && editedPatient ? editedPatient : basePatient;

  const lastVisitLabel = currentPatient.lastVisit
    ? new Date(currentPatient.lastVisit).toLocaleDateString()
    : 'N/A';

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-green-600" />
            </div>
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-3xl font-bold">{currentPatient.name}</h1>
                {currentPatient.aiCreated && (
                  <span className="px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full flex items-center space-x-1">
                    <Bot className="w-3 h-3" />
                    <span>AI Created</span>
                  </span>
                )}
              </div>
              <p className="text-green-100 text-sm">Patient ID: {currentPatient.id}</p>
              {currentPatient.createdAt && (
                <p className="text-green-100 text-xs mt-1">
                  Member since {new Date(currentPatient.createdAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2 px-3 py-2 bg-white bg-opacity-20 rounded-lg">
              <Globe className="w-4 h-4" />
              {isEditing && editedPatient ? (
                <select
                  value={editedPatient.preferred_language ?? 'en'}
                  onChange={(e) =>
                    setEditedPatient({
                      ...editedPatient,
                      preferred_language: e.target.value as Langs,
                    })
                  }
                  className="text-sm font-medium bg-white text-green-700 rounded px-2 py-1 focus:outline-none"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="yo">Yoruba</option>
                </select>
              ) : (
                <span className="text-sm font-medium">
                  {languageCodeToName(String(currentPatient.preferred_language))}
                </span>
              )}
            </div>
            {!isEditing ? (
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-white text-green-700 rounded-lg hover:bg-green-50 transition-colors flex items-center space-x-2 font-semibold"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  disabled={isUpdatingUser}
                  className={`px-4 py-2 bg-white text-green-700 rounded-lg transition-colors flex items-center space-x-2 font-semibold ${
                    isUpdatingUser ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-50'
                  }`}
                >
                  {isUpdatingUser ? (
                    <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{isUpdatingUser ? 'Saving...' : 'Save'}</span>
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isUpdatingUser}
                  className={`px-4 py-2 bg-red-600 text-white rounded-lg transition-colors flex items-center space-x-2 font-semibold ${
                    isUpdatingUser ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'
                  }`}
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <QuickStat
            icon={<Calendar className="w-5 h-5" />}
            label="Last Visit"
			value={lastVisitLabel}
          />
          <QuickStat
            icon={<Phone className="w-5 h-5" />}
            label="Total Calls"
            value={patientCalls.length.toString()}
          />
          <QuickStat
            icon={<MessageSquare className="w-5 h-5" />}
            label="Messages"
            value={patientMessages.length.toString()}
          />
          <QuickStat
            icon={<FileText className="w-5 h-5" />}
            label="Treatment Plans"
            value={currentPatient.treatmentPlans.length.toString()}
          />
        </div>
      </div>

      {/* Medical Alerts */}
      {currentPatient.aiSuggestedMedicalAlerts && currentPatient.aiSuggestedMedicalAlerts.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-900 mb-2">AI-Suggested Medical Alerts</h3>
              <div className="space-y-2">
                {currentPatient.aiSuggestedMedicalAlerts.map((alert) => (
                  <div key={alert.id} className="bg-white rounded p-3 border border-yellow-200">
                    <p className="text-sm font-medium text-yellow-900">{alert.alert}</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Source: {alert.source} • Confidence: {Math.round(alert.confidence * 100)}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <TabButton
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
              icon={<User className="w-5 h-5" />}
              label="Overview"
            />
            <TabButton
              active={activeTab === 'clinical'}
              onClick={() => setActiveTab('clinical')}
              icon={<Stethoscope className="w-5 h-5" />}
              label="Clinical & EDR"
            />
            <TabButton
              active={activeTab === 'communications'}
              onClick={() => setActiveTab('communications')}
              icon={<MessageSquare className="w-5 h-5" />}
              label="Communications"
            />
            <TabButton
              active={activeTab === 'education'}
              onClick={() => setActiveTab('education')}
              icon={<BookOpen className="w-5 h-5" />}
              label="Education"
            />
            <TabButton
              active={activeTab === 'billing'}
              onClick={() => setActiveTab('billing')}
              icon={<FileText className="w-5 h-5" />}
              label="Billing"
            />
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <OverviewTab patient={currentPatient} isEditing={isEditing} onChange={setEditedPatient} />
          )}
          {activeTab === 'clinical' && (
            <ClinicalTab patient={currentPatient} />
          )}
          {activeTab === 'communications' && (
            <CommunicationHistory patientId={+currentPatient.id} messages={patientMessages} />
          )}
          {activeTab === 'education' && (
            <EducationalHistory patient={currentPatient} />
          )}
          {activeTab === 'billing' && (
            <BillingTab 
              patient={currentPatient}
              billing={billing}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function QuickStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-white bg-opacity-20 rounded-lg p-3">
      <div className="flex items-center space-x-2 mb-1">
        {icon}
        <span className="text-sm text-green-100">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center space-x-2 px-6 py-4 font-medium transition-colors ${
        active
          ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}




